import i18n from '@/utils/i18n';
import React, { useRef } from 'react';
import WheyIcon from '@assets/icons/whey.svg';
import { BaseOfflineModeBanner } from '@/components/base-offline-mode-banner';
import { createStyles } from '@/contexts/theme-context';
import { DRAWER_HEADER_HEIGHT, SCREEN_PADDING } from '@/styles';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { Image, Platform, StatusBar, View } from 'react-native';
import { InboundDetailSplitContainer } from './inbound-detail-split-container';
import { InboundListScreenHeader } from './inbound-list-screen-header';
import { InboundListSplitPageContainer } from './inbound-list-split-page-container';
import { OrderInboundOption } from './types';
import { OrderInboundsList } from './order-inbounds-list';
import { PAGE_CONTAINER_PADDING_HORIZONTAL, skeletonData } from './utils';
import { PageContainer } from '@/components/page-container';
import { RecyclerListRef } from '@/components/recycler-list';
import { useLayoutContext } from '@/contexts/layout-context';

export const InboundOfflineScreen = () => {
  const { isLargeLayout, isSmallLayout, isSmallTabletLayout } =
    useLayoutContext();
  const [styles, theme] = useStyles();
  const listRef = useRef<RecyclerListRef<OrderInboundOption>>(null);

  const loadingList = (
    <OrderInboundsList
      isLoading
      ListHeaderComponent={<InboundListScreenHeader />}
      listRef={listRef}
      onSelect={() => {}}
      sections={skeletonData}
    />
  );

  const header = (
    <View
      style={[
        styles.header,
        Platform.OS === 'web' ? null : styles.headerNative,
      ]}
    >
      <View style={styles.logoWrapper}>
        <Image
          resizeMode="contain"
          source={{ uri: theme.images.welcomeLogoUrl }}
          style={styles.logoImageDesktop}
          testID="supplier-logo"
        />
      </View>
      <BaseOfflineModeBanner containerStyle={styles.offlineModeContainer} />
    </View>
  );

  return isSmallLayout ? (
    <PageContainer
      navigationHeaderProps={{
        contentHeader: <BaseOfflineModeBanner />,
        showShadow: false,
      }}
    >
      {loadingList}
    </PageContainer>
  ) : isSmallTabletLayout ? (
    <>
      {header}
      <PageContainer
        navigationHeaderProps={{
          showShadow: false,
        }}
      >
        {loadingList}
      </PageContainer>
    </>
  ) : (
    <>
      {header}
      <InboundListSplitPageContainer
        navigationHeaderProps={{
          showBackButton: false,
        }}
        primaryComponent={
          <>
            <StatusBar
              backgroundColor={theme.colors.white}
              barStyle="dark-content"
            />
            {loadingList}
          </>
        }
        skipPanelSpacing={isLargeLayout}
      >
        <InboundDetailSplitContainer
          detailContent={
            <EmptyStateMinimal
              containerStyle={styles.emptyStateContainer}
              ImageComponent={WheyIcon}
              text={i18n.t('network_status_error')}
            />
          }
        />
      </InboundListSplitPageContainer>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  emptyStateContainer: {
    backgroundColor: theme.colors.white,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    height: DRAWER_HEADER_HEIGHT,
    justifyContent: 'space-between',
  },
  headerNative: {
    marginTop: SCREEN_PADDING,
  },
  logoImageDesktop: {
    height: 44,
  },
  logoWrapper: {
    backgroundColor: theme.colors.white,
    width: 185,
  },
  offlineModeContainer: {
    paddingHorizontal: PAGE_CONTAINER_PADDING_HORIZONTAL,
  },
}));
